import React, { useState } from 'react'
import {
    Form,
    Input,
    InputNumber,
    Select,
    Row,
    Col,
    Spin,
} from 'antd'

import { PlusOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons'
import { useDiagnoses } from '../../../hooks/useDiagnoses'

import {
    StyledModal,
    SectionHeading,
    IngredientRow,
    IngredientHeader,
    ColLabel,
    RemoveButton,
    AddIngredientButton,
    SaveButton,
    CancelButton,
} from './styles'

export const CompoundFormModal = ({
    compound,
    dispenseUnits,
    onClose,
    onSubmit,
}) => {
    const [form] = Form.useForm()
    const [saving, setSaving] = useState(false)

    const isEditing = !!compound

    const initialValues = isEditing
        ? {
            name: compound.name,
            dispenseUnitId: compound.dispenseUnitId,
            schedule: compound.schedule,
            ingredients: compound.ingredients ?? [],
            primaryDiagnosisId: compound.firstPrescriptionDiagnosis?.primaryDiagnosisId ?? null,
            secondaryDiagnosisId: compound.secondPrescriptionDiagnosis?.primaryDiagnosisId ?? null,
        }
        : { ingredients: [] }

    const {
        options: primaryOptions,
        loading: primaryLoading,
        search: searchPrimary,
    } = useDiagnoses()

    const {
        options: secondaryOptions,
        loading: secondaryLoading,
        search: searchSecondary,
    } = useDiagnoses()

    // Track the full selected diagnosis objects so the name survives a save
    // without requiring the user to re-search. Seeded from stored data on edit.
    const [selectedPrimary, setSelectedPrimary] = useState(
        compound?.firstPrescriptionDiagnosis?.primaryDiagnosisId != null
            ? {
                diagnosisId: compound.firstPrescriptionDiagnosis.primaryDiagnosisId,
                primaryDiagnosisName: compound.firstPrescriptionDiagnosis.primaryDiagnosisName,
            }
            : null
    )
    const [selectedSecondary, setSelectedSecondary] = useState(
        compound?.secondPrescriptionDiagnosis?.primaryDiagnosisId != null
            ? {
                diagnosisId: compound.secondPrescriptionDiagnosis.primaryDiagnosisId,
                primaryDiagnosisName: compound.secondPrescriptionDiagnosis.primaryDiagnosisName,
            }
            : null
    )

    const handleSave = async () => {
        try {
            const values = await form.validateFields()
            setSaving(true)

            const { primaryDiagnosisId, secondaryDiagnosisId, ...rest } = values
            const payload = {
                ...rest,
                firstPrescriptionDiagnosis: primaryDiagnosisId != null
                    ? {
                        primaryDiagnosisId,
                        primaryDiagnosisName: selectedPrimary?.primaryDiagnosisName,
                    }
                    : undefined,
                secondPrescriptionDiagnosis: secondaryDiagnosisId != null
                    ? {
                        primaryDiagnosisId: secondaryDiagnosisId,
                        primaryDiagnosisName: selectedSecondary?.primaryDiagnosisName,
                    }
                    : undefined,
            }

            const success = await onSubmit(payload)
            if (success) onClose()
        } catch {
            // validation errors are shown inline — stay open
        } finally {
            setSaving(false)
        }
    }

    // Both compound-level dispense type and ingredient-level unit of measure
    // use the numeric StandardDispenseUnitTypeID as the stored value.
    const dispenseUnitOptions = dispenseUnits.map(u => (
        <Select.Option key={u.StandardDispenseUnitTypeID} value={u.StandardDispenseUnitTypeID}>
            {u.Name}
        </Select.Option>
    ))

    return (
        <StyledModal
            title={isEditing ? `Edit: ${compound.name}` : 'Add Compound'}
            visible
            onCancel={onClose}
            destroyOnClose
            centered
            width={700}
            footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <CancelButton id="compound-form-cancel-btn" icon={<CloseOutlined />} onClick={onClose}>
                        Cancel
                    </CancelButton>
                    <SaveButton
                        id="compound-form-save-btn"
                        icon={<SaveOutlined />}
                        loading={saving}
                        onClick={handleSave}
                    >
                        {isEditing ? 'Save Changes' : 'Create Compound'}
                    </SaveButton>
                </div>
            }
        >
            <Form form={form} layout="vertical" initialValues={initialValues}>
                <Form.Item
                    name="name"
                    label="Name"
                    rules={[{ required: true, message: 'Name is required' }]}
                >
                    <Input id="compound-form-name-input" placeholder="Compound name" />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={14}>
                        <Form.Item name="dispenseUnitId" label="Dispense Type">
                            <Select
                                id="compound-form-dispense-unit-select"
                                placeholder="Select dispense type"
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                            >
                                {dispenseUnitOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={10}>
                        <Form.Item name="schedule" label="Schedule">
                            <InputNumber
                                id="compound-form-schedule-input"
                                min={0}
                                max={5}
                                placeholder="0–5"
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <SectionHeading>Diagnoses</SectionHeading>

                <Form.Item name="primaryDiagnosisId" label="Primary Diagnosis">
                    <Select
                        placeholder="Search by ICD code or name..."
                        showSearch
                        allowClear
                        filterOption={false}
                        onSearch={searchPrimary}
                        notFoundContent={primaryLoading ? <Spin size="small" /> : null}
                        onChange={(val) => {
                            if (val == null) {
                                setSelectedPrimary(null)
                                setSelectedSecondary(null)
                                form.setFieldsValue({ secondaryDiagnosisId: null })
                            } else {
                                const found = primaryOptions.find(d => d.diagnosisId === val)
                                setSelectedPrimary(found
                                    ? { diagnosisId: found.diagnosisId, primaryDiagnosisName: found.diagnosisName }
                                    : null)
                            }
                        }}
                    >
                        {primaryOptions.map(d => (
                            <Select.Option key={d.diagnosisId} value={d.diagnosisId}>
                                {d.diagnosisCode} — {d.diagnosisName}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item shouldUpdate noStyle>
                    {({ getFieldValue }) => (
                        <Form.Item
                            name="secondaryDiagnosisId"
                            label="Secondary Diagnosis"
                            dependencies={['primaryDiagnosisId']}
                            rules={[
                                ({ getFieldValue: gfv }) => ({
                                    validator(_, value) {
                                        if (value != null && gfv('primaryDiagnosisId') == null) {
                                            return Promise.reject('A primary diagnosis is required before adding a secondary.')
                                        }
                                        return Promise.resolve()
                                    },
                                }),
                            ]}
                        >
                            <Select
                                placeholder="Search by ICD code or name..."
                                showSearch
                                allowClear
                                filterOption={false}
                                onSearch={searchSecondary}
                                notFoundContent={secondaryLoading ? <Spin size="small" /> : null}
                                disabled={!getFieldValue('primaryDiagnosisId')}
                                onChange={(val) => {
                                    if (val != null) {
                                        const found = secondaryOptions.find(d => d.diagnosisId === val)
                                        setSelectedSecondary(found
                                            ? { diagnosisId: found.diagnosisId, primaryDiagnosisName: found.diagnosisName }
                                            : null)
                                    } else {
                                        setSelectedSecondary(null)
                                    }
                                }}
                            >
                                {secondaryOptions.map(d => (
                                    <Select.Option key={d.diagnosisId} value={d.diagnosisId}>
                                        {d.diagnosisCode} — {d.diagnosisName}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                </Form.Item>

                <SectionHeading>Ingredients</SectionHeading>

                <Form.List name="ingredients">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.length > 0 && (
                                <IngredientHeader>
                                    <ColLabel>Unit of Measure</ColLabel>
                                    <ColLabel>Quantity</ColLabel>
                                    <ColLabel>Strength</ColLabel>
                                    <ColLabel>Name</ColLabel>
                                    <span />
                                </IngredientHeader>
                            )}

                            {fields.map(({ key, name }) => (
                                <IngredientRow key={key}>
                                    <Form.Item name={[name, 'dispenseUnitId']} style={{ margin: 0 }}>
                                        <Select
                                            placeholder="Unit"
                                            allowClear
                                            showSearch
                                            filterOption={(input, option) =>
                                                option.children.toLowerCase().includes(input.toLowerCase())
                                            }
                                        >
                                            {dispenseUnitOptions}
                                        </Select>
                                    </Form.Item>
                                    <Form.Item name={[name, 'quantity']} style={{ margin: 0 }}>
                                        <InputNumber
                                            min={0}
                                            stringMode
                                            placeholder="e.g. 1"
                                            style={{ width: '100%' }}
                                        />
                                    </Form.Item>
                                    <Form.Item name={[name, 'strength']} style={{ margin: 0 }}>
                                        <Input placeholder="e.g. 2.5mg/mL" />
                                    </Form.Item>
                                    <Form.Item name={[name, 'freeText']} style={{ margin: 0 }}>
                                        <Input placeholder="Name" />
                                    </Form.Item>
                                    <RemoveButton onClick={() => remove(name)} />
                                </IngredientRow>
                            ))}

                            <AddIngredientButton
                                icon={<PlusOutlined />}
                                onClick={() => add({})}
                            >
                                Add Ingredient
                            </AddIngredientButton>
                        </>
                    )}
                </Form.List>
            </Form>
        </StyledModal>
    )
}
